/**
 * Корректировка остатка (макет 14.10.2026, экраны 5–8).
 *
 * Одно поле — сколько на счёте сейчас, у кредитки — сколько должен. Под ним
 * то, что записано, и разница. Спрашивать «что это было» не нужно: разница
 * записывается «Корректировкой», не тратой и не доходом. Захочет человек —
 * потом превратит её в трату («Это были траты»).
 *
 * Так делает большинство приложений учёта. Главные жалобы на них учтены:
 * корректировку можно поправить, удалить, у неё есть дата и заметка, а в
 * отчёты она не попадает тратой — у неё своя строка в «Итогах».
 */
import { useState } from 'react';
import { add, formatMoney, neg, sub, ZERO, type Account, type Money, type PlainDate } from '@skoka/core';
import {
  adjustBalance, adjustmentToExpense, deleteTransaction, formatAmountInput, parseAmountInput, todayPd,
  updateAdjustment, type AppState, type DeletedTx, type Tx,
} from './store.js';
import { AccountGlyph } from './account-glyph.js';
import { AmountInput } from './amount.js';
import { CardWhen, NoteField } from './card-fields.js';
import { CategoryField } from './operation-fields.js';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';
import { dayMonth } from './text.js';

/** Минус — со знаком «−», плюс — с «+». */
const signed = (m: Money): string => (m < ZERO ? `−${formatMoney(neg(m))}` : m > ZERO ? `+${formatMoney(m)}` : formatMoney(m));

const whenText = (date: string, time: string, today: string): string =>
  `${date === today ? 'Сегодня' : dayMonth(date, today)}, ${time}`;

const nowTime = (): string => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Закреплено на счёте под платежи и цели — без запаса стабильного бюджета: у него своя сверка. */
const reservedOn = (state: AppState, id: string): Money =>
  add(ZERO, ...state.reserves.filter((r) => r.accountId === id && r.forKind !== 'stable').map((r) => r.amount));

const AccountLine = ({ account }: { account: Account }) => (
  <div className="adj-acct"><AccountGlyph account={account} size="s" />{account.name}</div>
);

/** Когда остаток сверяли: последняя корректировка или дата, на которую его назвали. */
export function checkedOn(state: AppState, accountId: string): string | null {
  const acc = state.accounts.find((a) => a.id === accountId);
  return state.transactions
    .filter((t) => t.type === 'adjustment' && t.accountId === accountId)
    .map((t) => t.occurredDate).sort().pop() ?? acc?.balanceAsOf ?? null;
}

/** «сверен сегодня», «сверен 12 октября · 2 недели» — давно (больше недели) жёлтым. */
export function checkedText(state: AppState, accountId: string): { text: string; warn: boolean } | null {
  const at = checkedOn(state, accountId);
  if (!at) return null;
  const days = Math.round((Date.parse(state.today) - Date.parse(at)) / 864e5);
  const ago = days >= 14 ? ` · ${Math.floor(days / 7)} ${days < 35 ? 'недели' : 'недель'}` : '';
  return { text: `сверен ${at === state.today ? 'сегодня' : dayMonth(at, state.today)}${ago}`, warn: days > 7 };
}

export function AdjustSheet({ state, accountId, onClose, onDone, onPick, shots }: {
  state: AppState; accountId: string; onClose: () => void; onDone: () => void;
  /** Сменить счёт — строка счёта сверху становится выбором (27.10.2026). */
  onPick?: () => void;
  /**
   * «Все остатки разом» — по скриншотам из банка. Pro: выбор файлов; без
   * Pro — экран Pro про остатки.
   */
  shots?: { locked: boolean; onLocked: () => void; onFiles: (files: File[]) => void };
}) {
  const acc = state.accounts.find((a) => a.id === accountId);
  const today = todayPd();
  const isCard = acc?.kind === 'credit_card';
  const [value, setValue] = useState('');
  const [date, setDate] = useState<string>(today);
  const [time, setTime] = useState(nowTime());
  // Время, которое человек не трогал, — «сейчас» по часам операций: иначе
  // местное «12:20» легло бы позже трат, записанных следом.
  const [moved, setMoved] = useState(false);
  const [note, setNote] = useState('');
  const [trim, setTrim] = useState(true);
  const [fees, setFees] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!acc) return null;

  const typed = value.trim() !== '';
  const entered = parseAmountInput(value);
  const target = isCard ? neg(entered) : entered;
  // Записанное — на сейчас. Корректировка задним числом посчитается от
  // остатка на тот момент; здесь — подсказка, точное число даст запись.
  const diff = sub(target, acc.balance);
  const held = reservedOn(state, acc.id);
  const short = !isCard && typed && held > ZERO && target < held;
  const debtGrew = isCard && diff < ZERO;

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await adjustBalance({
        accountId: acc.id, target, date: (moved ? date : today) as PlainDate,
        ...(moved ? { time } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(short && trim ? { trim: true } : {}),
        ...(debtGrew && fees ? { asFees: true } : {}),
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось скорректировать');
      setBusy(false);
    }
  };

  return (
    // Вид листа записи (04.11.2026): строка «Когда» и заметка — его карточки, без его цветов они висели без фона.
    <SheetWrap title={isCard ? 'Скорректировать долг' : 'Скорректировать остаток'} className="rcpt adjsheet" onClose={onClose}>
      <div className="form">
        {onPick ? (
          <button className="adj-acct pick" onClick={onPick} aria-label={`Счёт: ${acc.name}. Сменить`}>
            <AccountGlyph account={acc} size="s" /><span className="nm">{acc.name}</span>
            {checkedText(state, acc.id) && <span className={`sub${checkedText(state, acc.id)!.warn ? ' warn' : ''}`}>{checkedText(state, acc.id)!.text}</span>}
            <span className="ch"><Icon name="chevron-right" size={13} /></span>
          </button>
        ) : <AccountLine account={acc} />}
        <label className="adj-amt">
          <span>{isCard ? 'Сколько должен по карте' : 'Сколько сейчас на счёте'}</span>
          <AmountInput value={value} onChange={setValue} placeholder={formatAmountInput(String((isCard ? neg(acc.balance) : acc.balance) / 100))}
            label={isCard ? 'Сколько должен по карте' : 'Сколько сейчас на счёте'} className="num" />
        </label>
        <div className="adj-flow"><span>{isCard ? 'Долг в приложении' : 'Было в приложении'}</span>
          <b className="num">{formatMoney(isCard ? neg(acc.balance) : acc.balance)}</b></div>
        {typed && (
          <div className="adj-flow">
            <span>{isCard ? (diff < ZERO ? 'Долг вырос на' : diff > ZERO ? 'Долг уменьшился на' : 'Разница') : 'Разница'}</span>
            <b className={`num${diff < ZERO ? ' neg' : diff > ZERO ? ' pos' : ''}`}>
              {isCard && diff !== ZERO ? formatMoney(diff < ZERO ? neg(diff) : diff) : signed(diff)}</b>
          </div>
        )}
        {debtGrew && (
          <label className="check">
            <input type="checkbox" checked={fees} onChange={(e) => setFees(e.target.checked)} />
            Это проценты или комиссия — записать тратой
          </label>
        )}
        {short && (
          <div className="warnrow"><Icon name="triangle-exclamation" size={15} />
            <div>На счёте закреплено {formatMoney(held)} под платежи и цели, а останется {formatMoney(target)}.
              <label className="check">
                <input type="checkbox" checked={trim} onChange={(e) => setTrim(e.target.checked)} />
                Уменьшить закреплённое до {formatMoney(clampMoney(target))}
              </label></div></div>
        )}
        <p className="adj-note">
          {isCard && diff > ZERO
            ? 'Если долг стал меньше, потому что платил с другого своего счёта, — запиши перевод: тогда уменьшится и тот счёт.'
            : debtGrew && fees
              ? 'Запишется тратой «Проценты и комиссии» — в расходах и «Аналитике».'
              : 'В траты не попадёт. Найдутся пропущенные операции — разница уменьшится сама.'}
        </p>
        <div className="rccard rcsel">
          <CardWhen text={whenText(date, time, today)} date={date} time={time} max={`${today}T23:59`}
            onChange={(d, t) => { setDate(d); setTime(t); setMoved(true); }} />
        </div>
        <NoteField value={note} onChange={setNote} />
        {shots && (shots.locked ? (
          <button className="shotlead" onClick={shots.onLocked}>
            <span className="addtile-i"><Icon name="images" size={20} /></span>
            <span className="t"><b>Все остатки разом<b className="protag">PRO</b></b><span>по скриншотам из приложений банков</span></span>
            <span className="ch"><Icon name="chevron-right" size={13} /></span>
          </button>
        ) : (
          <label className="shotlead">
            <span className="addtile-i"><Icon name="images" size={20} /></span>
            <span className="t"><b>Все остатки разом</b><span>по скриншотам из приложений банков</span></span>
            <span className="ch"><Icon name="chevron-right" size={13} /></span>
            <input type="file" accept="image/*" multiple className="filehit" onChange={(e) => {
              const f = [...(e.target.files ?? [])]; e.target.value = '';
              if (f.length) shots.onFiles(f);
            }} />
          </label>
        ))}
        {err && <div className="note warn">{err}</div>}
        <button className="btn pri" disabled={busy || !typed || diff === ZERO} onClick={() => void save()}>
          {typed && diff === ZERO ? 'Остаток совпадает' : busy ? 'Сохраняю…' : 'Скорректировать'}</button>
      </div>
    </SheetWrap>
  );
}

const clampMoney = (m: Money): Money => (m < ZERO ? ZERO : m);

/** Нажали на корректировку в «Операциях»: поправить, сделать тратой, удалить. */
export function AdjustmentSheet({ state, tx, onClose, onSaved, onDeleted }: {
  state: AppState; tx: Tx; onClose: () => void; onSaved: () => void; onDeleted: (deleted: DeletedTx) => void;
}) {
  const acc = state.accounts.find((a) => a.id === tx.accountId);
  const today = todayPd();
  const isCard = acc?.kind === 'credit_card';
  // Названный остаток. У старой записи его может не быть — тогда разница
  // показывается без него, и поле правит её от записанного.
  const known = tx.target;
  const shownTarget = known ?? ZERO;
  const [value, setValue] = useState(known !== undefined
    ? formatAmountInput(String((isCard ? neg(shownTarget) : shownTarget) / 100)) : '');
  const [date, setDate] = useState<string>(tx.occurredDate);
  const [time, setTime] = useState(tx.occurredAt.slice(11, 16) || '12:00');
  const [note, setNote] = useState(tx.note ?? '');
  const [categories, setCategories] = useState(state.categories);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const typed = value.trim() !== '';
  const entered = parseAmountInput(value);
  const target = isCard ? neg(entered) : entered;
  // Разница после правки: прежняя плюс изменение названного остатка. Дату
  // меняют редко; если сменили — точное число посчитает запись.
  const diff = known !== undefined && typed ? add(tx.amount, sub(target, known)) : tx.amount;
  const refined = tx.initial !== undefined && tx.initial !== tx.amount;

  const run = async (work: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await work(); } catch (e) { setErr(e instanceof Error ? e.message : 'Не получилось'); setBusy(false); }
  };

  return (
    <SheetWrap title="Корректировка" onClose={onClose}
      tip="Корректировка — не трата и не доход: разница между записанным и тем, что было на счёте. В «Итогах» у неё своя строка.">
      <div className="form">
        {acc && <AccountLine account={acc} />}
        <label className="adj-amt">
          <span>{isCard ? 'Сколько был должен' : 'Сколько было на счёте'}</span>
          <AmountInput value={value} onChange={setValue} placeholder="0" className="num"
            label={isCard ? 'Сколько был должен' : 'Сколько было на счёте'} />
        </label>
        <div className="adj-flow"><span>Разница с записанным</span>
          <b className={`num${diff < ZERO ? ' neg' : diff > ZERO ? ' pos' : ''}`}>{signed(diff)}</b></div>
        {refined && (
          <div className="adj-flow"><span>Сначала была</span><b className="num">{signed(tx.initial!)}</b></div>
        )}
        {refined && <p className="adj-note">Разница уменьшилась: задним числом записаны операции, которые её объясняют.</p>}
        <div className="rccard rcsel">
          <CardWhen text={whenText(date, time, today)} date={date} time={time} max={`${today}T23:59`}
            onChange={(d, t) => { setDate(d); setTime(t); }} />
        </div>
        <NoteField value={note} onChange={setNote} />
        {/* Корректировка в минус может оказаться тратами: выбрал категорию —
            она стала обычной тратой той же суммы. Остаток не меняется. */}
        {tx.amount < ZERO && (
          <CategoryField categories={categories} kind="expense" value="" onUpdated={setCategories}
            onChange={(id) => {
              const c = categories.find((x) => x.id === id);
              void run(async () => { await adjustmentToExpense(tx.id, id, c?.defaultPart === 'want' ? 'want' : 'need'); onSaved(); });
            }}
            trigger={(open) => (
              <button className="adj-spend" disabled={busy} onClick={open}>
                <span className="t"><b>Это были траты</b>
                  <span>Выбери категорию — станет обычной тратой на {formatMoney(neg(tx.amount))}</span></span>
                <Icon name="chevron-right" size={14} />
              </button>
            )} />
        )}
        {err && <div className="note warn">{err}</div>}
        <button className="btn pri" disabled={busy || (known !== undefined && !typed)}
          onClick={() => void run(async () => {
            await updateAdjustment(tx.id, {
              ...(typed ? { target } : {}),
              ...(date !== tx.occurredDate || time !== tx.occurredAt.slice(11, 16) ? { date: date as PlainDate, time } : {}),
              note: note.trim(),
            });
            onSaved();
          })}>{busy ? 'Сохраняю…' : 'Сохранить'}</button>
        <button className="textact danger" disabled={busy}
          onClick={() => void run(async () => {
            const deleted = await deleteTransaction(tx.id);
            if (deleted) onDeleted(deleted); else onSaved();
          })}>Удалить корректировку</button>
      </div>
    </SheetWrap>
  );
}
